import { sendEventMetric } from "@expert/cloudwatch-metrics";
import { useCallback, useMemo } from "react";

import { getRootDispatcher } from "@/analytics";
import { env } from "@/config";
import { usePartner } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useBoundedFirstLoadPending, useFeatureFlag } from "@/shared-utils";

import type { SessionSummaryConfig } from "./getSessionSummary";

import { selectSummarySource } from "./selectSummarySource";

/** Longest we'll gate the summary fetch on the Simplr routing flags before giving up and treating
 *  them as resolved (falling back to their default/last-known value). Bounds an otherwise-indefinite
 *  hang if the flag requests never settle. */
const SIMPLR_FLAGS_LOAD_TIMEOUT_MS = 5000;

interface SimplrSummaryRouting {
    /** Pass straight to {@link getSessionSummary} as its `config` argument. */
    summaryConfig: SessionSummaryConfig;
    /**
     * True while this partner's Simplr flags have not resolved yet. Callers MUST wait for this to
     * clear before fetching — otherwise a call can end before the flag requests resolve and the
     * summary silently falls back to GAIA regardless of the flags' actual value.
     */
    isSimplrSummaryFlagsLoading: boolean;
}

/**
 * Resolves the Simplr dark-launch routing decision for the session summary: which backend serves the
 * summary (live cutover) and whether to fetch Simplr alongside GAIA to compare (shadow).
 *
 * Shared by both summary UIs on purpose. The updated `SessionSummary` flow is gated on
 * `isUpdatedEndSessionFlowEnabled`, which in prod only matches Verizon, so every other partner --
 * Staples included -- renders `DeprecatedSessionSummary`. When this wiring lived only in `useSummary`
 * those partners never ran the shadow or the cutover at all, no matter what the Simplr flags said.
 */
export function useSimplrSummaryRouting(): SimplrSummaryRouting {
    const logger = useLogger({ module: "sessionSummary" });
    const partner = usePartner();

    const { data: isSimplrSessionSummaryDarkLaunchEnabled = false, isLoading: isSimplrDarkLaunchFlagLoading } =
        useFeatureFlag<boolean>("isSimplrSessionSummaryDarkLaunchEnabled", { partner });
    const { data: isSimplrSessionSummaryLiveEnabled = false, isLoading: isSimplrLiveFlagLoading } =
        useFeatureFlag<boolean>("isSimplrSessionSummaryLiveEnabled", { partner });

    const handleSimplrFlagsLoadTimeout = useCallback(() => {
        logger.warn(
            { partner, timeoutMs: SIMPLR_FLAGS_LOAD_TIMEOUT_MS },
            "Simplr summary routing flags did not resolve in time; proceeding with default/last-known value",
        );
        void sendEventMetric("sessionSummarySimplr.flagLoadTimeout");
    }, [logger, partner]);

    // These are react-query flags keyed on `partner`, so `isLoading` is only true while this
    // partner's value hasn't been fetched yet in this session (the very first render, or the
    // first time a newly-encountered partner is seen).
    const isSimplrSummaryFlagsLoading = useBoundedFirstLoadPending(
        isSimplrDarkLaunchFlagLoading || isSimplrLiveFlagLoading,
        SIMPLR_FLAGS_LOAD_TIMEOUT_MS,
        handleSimplrFlagsLoadTimeout,
    );

    // Dark launch: pick which backend serves the summary. The live cutover returns the Simplr summary
    // in place of GAIA when isSimplrSessionSummaryLiveEnabled targets this session; otherwise GAIA stays
    // the source of truth. When only the shadow flag is on (and a Simplr URL is configured), GAIA is still
    // returned but the Simplr summary is fetched alongside it to compare/report -- never driving the UI.
    const source = selectSummarySource({
        isSimplrSessionSummaryLiveEnabled,
        simplrApiUrl: env.simplrApiUrl,
        partner,
        isProdMode: env.isProdMode,
    });
    const shadowEnabled = isSimplrSessionSummaryDarkLaunchEnabled && Boolean(env.simplrApiUrl) && source === "gaia";

    const onComparison = useCallback((result: Parameters<NonNullable<SessionSummaryConfig["onComparison"]>>[0]) => {
        void getRootDispatcher().dispatchBusinessEvent("SimplrSessionSummaryShadowComparison", { ...result });
    }, []);

    const summaryConfig = useMemo(
        () => ({ source, shadowEnabled, onComparison }),
        [source, shadowEnabled, onComparison],
    );

    return useMemo(
        () => ({ summaryConfig, isSimplrSummaryFlagsLoading }),
        [summaryConfig, isSimplrSummaryFlagsLoading],
    );
}
